import React, { useState, useEffect } from 'react';
import { Plus, Search, UserPlus, Phone, Mail, Building, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    website: '',
    business_type: '',
    address: '',
    source: 'website',
    notes: '',
    social_media: {}
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leads', formData);
      toast.success('Lead added successfully!');
      setDialogOpen(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        website: '',
        business_type: '',
        address: '',
        source: 'website',
        notes: '',
        social_media: {}
      });
      fetchLeads();
    } catch (error) {
      console.error('Failed to add lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    }
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}`, { status: newStatus });
      toast.success('Status updated!');
      fetchLeads();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleConvertToClient = async (leadId) => {
    if (!window.confirm('Convert this lead to a client?')) return;

    try {
      await api.post(`/leads/${leadId}/convert`);
      toast.success('Lead converted to client successfully!');
      fetchLeads();
    } catch (error) {
      console.error('Failed to convert lead:', error);
      toast.error('Failed to convert lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.company_name && lead.company_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      new: 'badge-info',
      contacted: 'badge-warning',
      qualified: 'badge-success',
      converted: 'badge-success',
      lost: 'badge-danger'
    };
    return colors[status] || 'badge-info';
  };

  const getStatusIcon = (status) => {
    if (status === 'converted') return <CheckCircle className="w-4 h-4" />;
    if (status === 'lost') return <XCircle className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="leads-title">Lead Management</h2>
          <p className="text-gray-600">Track and convert potential clients</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-lead-button"
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    data-testid="lead-first-name-input"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    data-testid="lead-last-name-input"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="lead-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="lead-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  data-testid="lead-company-input"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  data-testid="lead-website-input"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <Label htmlFor="business_type">Business Type</Label>
                <Input
                  id="business_type"
                  data-testid="lead-business-type-input"
                  value={formData.business_type}
                  onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                />
              </div>
              
              {/* Social Media Links */}
              <div className="space-y-3 pt-2">
                <Label className="font-semibold">Social Media & Links</Label>
                <div>
                  <Label htmlFor="linkedin" className="text-sm text-gray-600">LinkedIn</Label>
                  <Input
                    id="linkedin"
                    value={formData.social_media.linkedin || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_media: { ...formData.social_media, linkedin: e.target.value }
                    })}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                <div>
                  <Label htmlFor="facebook" className="text-sm text-gray-600">Facebook</Label>
                  <Input
                    id="facebook"
                    value={formData.social_media.facebook || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_media: { ...formData.social_media, facebook: e.target.value }
                    })}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <Label htmlFor="twitter" className="text-sm text-gray-600">Twitter</Label>
                  <Input
                    id="twitter"
                    value={formData.social_media.twitter || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_media: { ...formData.social_media, twitter: e.target.value }
                    })}
                    placeholder="https://twitter.com/..."
                  />
                </div>
                <div>
                  <Label htmlFor="instagram" className="text-sm text-gray-600">Instagram</Label>
                  <Input
                    id="instagram"
                    value={formData.social_media.instagram || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_media: { ...formData.social_media, instagram: e.target.value }
                    })}
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <Label htmlFor="tiktok" className="text-sm text-gray-600">TikTok</Label>
                  <Input
                    id="tiktok"
                    value={formData.social_media.tiktok || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_media: { ...formData.social_media, tiktok: e.target.value }
                    })}
                    placeholder="https://tiktok.com/@..."
                  />
                </div>
                <div>
                  <Label htmlFor="podcast" className="text-sm text-gray-600">Podcast</Label>
                  <Input
                    id="podcast"
                    value={formData.social_media.podcast || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_media: { ...formData.social_media, podcast: e.target.value }
                    })}
                    placeholder="https://podcasts.apple.com/..."
                  />
                </div>
                <div>
                  <Label htmlFor="custom_link" className="text-sm text-gray-600">Custom Link</Label>
                  <Input
                    id="custom_link"
                    value={formData.social_media.custom_link || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_media: { ...formData.social_media, custom_link: e.target.value }
                    })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="source">Lead Source</Label>
                <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                  <SelectTrigger data-testid="lead-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="cold_call">Cold Call</SelectItem>
                    <SelectItem value="email">Email Campaign</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="lead-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <Button
                data-testid="submit-lead-button"
                type="submit"
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                Add Lead
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              data-testid="search-leads-input"
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter-select">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Leads Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredLeads.length === 0 ? (
          <div className="col-span-full glass rounded-xl p-12 text-center">
            <UserPlus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' ? 'No leads found' : 'No leads yet. Add your first lead!'}
            </p>
          </div>
        ) : (
          filteredLeads.map((lead, index) => (
            <div
              key={lead.id}
              className="glass rounded-xl p-6 hover-lift transition-all cursor-pointer"
              data-testid={`lead-card-${index}`}
              onClick={() => window.location.href = `/admin/leads/${lead.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-700 font-semibold text-lg">
                      {lead.first_name[0]}{lead.last_name[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">
                      {lead.first_name} {lead.last_name}
                    </h3>
                    <span className={`badge ${getStatusColor(lead.status)} text-xs`}>
                      {lead.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {lead.company_name && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Building className="w-4 h-4 mr-2" />
                    {lead.company_name}
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="w-4 h-4 mr-2" />
                  {lead.email}
                </div>
                {lead.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    {lead.phone}
                  </div>
                )}
              </div>

              {lead.notes && (
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{lead.notes}</p>
              )}

              <div className="flex items-center space-x-2">
                {lead.status !== 'converted' && lead.status !== 'lost' && (
                  <>
                    <Select value={lead.status} onValueChange={(value) => handleStatusUpdate(lead.id, value)}>
                      <SelectTrigger className="text-xs h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      data-testid={`convert-button-${index}`}
                      size="sm"
                      onClick={() => handleConvertToClient(lead.id)}
                      className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white text-xs"
                    >
                      <UserPlus className="w-3 h-3 mr-1" />
                      Convert
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Leads;
